import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowLeft, Loader2, Target, Plus, Trash2, Calendar, FileText, MessageSquare, Clock, Star, CheckCircle2, AlertCircle, PauseCircle, Download } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { FileUploader, FileList } from "@/components/FileUploader";
import { ChannelBadge } from "@/components/ChannelBadge";

const STATUS_LABELS = {
  not_started: { label: "Non avviata", color: "bg-slate-100 text-slate-700" },
  in_progress: { label: "In corso", color: "bg-[#EAF0FF] text-[#2D6BFF]" },
  on_hold: { label: "In attesa", color: "bg-amber-100 text-amber-700" },
  completed: { label: "Completata", color: "bg-[#E9F8F1] text-[#1F9D6A]" },
  blocked: { label: "Bloccata", color: "bg-red-100 text-red-700" },
};

export default function ProjectWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  // Forms
  const [newMilestone, setNewMilestone] = useState({ title: "", description: "", due_date: "", priority: "medium" });
  const [newKpi, setNewKpi] = useState({ name: "", description: "", unit: "%", initial_value: 0, target_value: 0, current_value: 0, frequency: "settimanale" });
  const [newTask, setNewTask] = useState({ title: "", milestone_id: "", due_date: "" });
  const [newTime, setNewTime] = useState({ date: new Date().toISOString().slice(0, 10), hours: 8, description: "" });

  const [docsKey, setDocsKey] = useState(0);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${id}`);
      setProject(r.data);
    } catch (e) {
      toast.error("Errore caricamento progetto");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [id]);

  const addMilestone = async () => {
    try {
      await api.post(`/projects/${id}/milestones`, newMilestone);
      setNewMilestone({ title: "", description: "", due_date: "", priority: "medium" });
      toast.success("Milestone creata");
      await refresh();
    } catch (e) { toast.error("Errore"); }
  };
  const updateMilestoneStatus = async (mId, status) => {
    try { await api.put(`/projects/milestones/${mId}`, { status, progress: status === "completed" ? 100 : undefined }); refresh(); }
    catch (e) { toast.error("Errore"); }
  };
  const deleteMilestone = async (mId) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/projects/milestones/${mId}`); refresh(); }
    catch (e) { toast.error("Errore"); }
  };
  const addKpi = async () => {
    try { await api.post(`/projects/${id}/kpis`, newKpi); setNewKpi({ name: "", description: "", unit: "%", initial_value: 0, target_value: 0, current_value: 0, frequency: "settimanale" }); toast.success("KPI creato"); refresh(); }
    catch (e) { toast.error("Errore"); }
  };
  const updateKpi = async (kId, value, note) => {
    try { await api.post(`/projects/kpis/${kId}/update`, { value: parseFloat(value), note }); toast.success("KPI aggiornato"); refresh(); }
    catch (e) { toast.error("Errore"); }
  };
  const deleteKpi = async (kId) => {
    if (!window.confirm("Eliminare KPI?")) return;
    try { await api.delete(`/projects/kpis/${kId}`); refresh(); }
    catch (e) { toast.error("Errore"); }
  };
  const addTask = async () => {
    try { await api.post(`/projects/${id}/tasks`, newTask); setNewTask({ title: "", milestone_id: "", due_date: "" }); refresh(); }
    catch (e) { toast.error("Errore"); }
  };
  const toggleTask = async (taskId, newStatus) => {
    try { await api.put(`/projects/tasks/${taskId}`, { status: newStatus }); refresh(); }
    catch (e) { toast.error("Errore"); }
  };
  const addTimeEntry = async () => {
    try { await api.post(`/projects/${id}/time-entries`, newTime); setNewTime({ date: new Date().toISOString().slice(0, 10), hours: 8, description: "" }); toast.success("Ore registrate"); refresh(); }
    catch (e) { toast.error("Errore"); }
  };

  if (loading || !project) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;

  const overallProgress = project.milestones?.length ? Math.round(project.milestones.reduce((s, m) => s + (m.progress || 0), 0) / project.milestones.length) : 0;
  const backHref = user?.role === "admin" ? "/admin/projects" : user?.role === "manager" ? "/manager/projects" : "/app/projects";

  return (
    <div>
      <Link to={backHref} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={14} /> Tutti i progetti</Link>

      <div className="cxo-card p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-[#E9F8F1] text-[#1F9D6A] border-0">Progetto {project.status}</Badge>
              <ChannelBadge channel={project.channel || "agency"} size="xs" />
              {project.sla_active && (
                <Badge className="bg-[#FFF6E6] text-[#B7791F] border-0 text-[10px]">SLA attivo</Badge>
              )}
            </div>
            <h1 className="mt-1 font-display text-2xl sm:text-3xl font-bold text-slate-900">{project.title}</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">{project.objective}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-600">
              <span><strong>Azienda:</strong> {project.company?.company_name}</span>
              <span><strong>Manager:</strong> {project.manager?.user?.full_name}</span>
              <span><strong>Pacchetto:</strong> {project.package}</span>
              <span><strong>Durata:</strong> {project.start_date} → {project.end_date}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-slate-500">Avanzamento</p>
              <p className="text-3xl font-display font-bold cxo-tabular text-slate-900">{overallProgress}%</p>
              <Progress value={overallProgress} className="mt-1 w-40" />
            </div>
            <a
              href={`${API_BASE}/pdf/project-report/${project.id}?auth=${encodeURIComponent(localStorage.getItem("cxoora_token") || "")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="download-project-report-btn"
            >
              <Button size="sm" variant="outline" className="gap-1.5"><Download size={14} /> Scarica report PDF</Button>
            </a>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto" data-testid="project-workspace-tabs">
          <TabsTrigger value="overview">Timeline</TabsTrigger>
          <TabsTrigger value="milestones">Milestone</TabsTrigger>
          <TabsTrigger value="kpi">KPI</TabsTrigger>
          <TabsTrigger value="tasks">Task</TabsTrigger>
          <TabsTrigger value="time">Ore/Giornate</TabsTrigger>
          <TabsTrigger value="docs">Documenti</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <div className="cxo-card p-6">
            <h3 className="font-display text-lg font-semibold text-slate-900">Timeline del progetto</h3>
            <p className="text-sm text-slate-600 mt-1">Le milestone in ordine cronologico.</p>
            <div className="mt-6 relative pl-7">
              <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200" />
              {project.milestones?.length > 0 ? project.milestones.map((m) => (
                <div key={m.id} className="relative mb-6">
                  <div className={`absolute -left-7 top-1 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center ${m.status === "completed" ? "bg-[#1F9D6A]" : m.status === "in_progress" ? "bg-[#2D6BFF]" : "bg-slate-300"}`}>
                    {m.status === "completed" && <CheckCircle2 size={12} className="text-white" />}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{m.due_date || "—"}</p>
                    <h4 className="text-base font-semibold text-slate-900">{m.title}</h4>
                    <p className="text-sm text-slate-600">{m.description}</p>
                    <Badge className={`mt-2 ${STATUS_LABELS[m.status]?.color || ""} border-0`}>{STATUS_LABELS[m.status]?.label}</Badge>
                  </div>
                </div>
              )) : <p className="text-sm text-slate-500">Nessuna milestone ancora.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="milestones" className="mt-5 space-y-4">
          <div className="cxo-card p-5" data-testid="milestone-create">
            <h3 className="text-sm font-semibold text-slate-900">Nuova milestone</h3>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
              <Input placeholder="Titolo" value={newMilestone.title} onChange={e => setNewMilestone({...newMilestone, title: e.target.value})} />
              <Input placeholder="Descrizione" value={newMilestone.description} onChange={e => setNewMilestone({...newMilestone, description: e.target.value})} />
              <Input type="date" value={newMilestone.due_date} onChange={e => setNewMilestone({...newMilestone, due_date: e.target.value})} />
              <Button onClick={addMilestone} disabled={!newMilestone.title} className="bg-[#2D6BFF] text-white"><Plus size={14} className="mr-1" /> Aggiungi</Button>
            </div>
          </div>
          <div data-testid="milestone-list" className="space-y-3">
            {project.milestones?.map(m => (
              <div key={m.id} className="cxo-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">{m.title}</h4>
                    <p className="text-sm text-slate-600 mt-1">{m.description}</p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500"><Calendar size={12} /> {m.due_date}</div>
                    <Progress value={m.progress || 0} className="mt-3" />
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={`${STATUS_LABELS[m.status]?.color} border-0`}>{STATUS_LABELS[m.status]?.label}</Badge>
                    <Select value={m.status} onValueChange={(v) => updateMilestoneStatus(m.id, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {user?.role === "admin" && <button onClick={() => deleteMilestone(m.id)} className="text-red-500 text-xs hover:text-red-700"><Trash2 size={12} /></button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="kpi" className="mt-5 space-y-4" data-testid="kpi-table">
          <div className="cxo-card p-5">
            <h3 className="text-sm font-semibold text-slate-900">Nuovo KPI</h3>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-6 gap-2">
              <Input placeholder="Nome" value={newKpi.name} onChange={e => setNewKpi({...newKpi, name: e.target.value})} />
              <Input placeholder="Unità" value={newKpi.unit} onChange={e => setNewKpi({...newKpi, unit: e.target.value})} />
              <Input type="number" placeholder="Iniziale" value={newKpi.initial_value} onChange={e => setNewKpi({...newKpi, initial_value: parseFloat(e.target.value) || 0})} />
              <Input type="number" placeholder="Target" value={newKpi.target_value} onChange={e => setNewKpi({...newKpi, target_value: parseFloat(e.target.value) || 0})} />
              <Input type="number" placeholder="Attuale" value={newKpi.current_value} onChange={e => setNewKpi({...newKpi, current_value: parseFloat(e.target.value) || 0})} />
              <Button onClick={addKpi} disabled={!newKpi.name} className="bg-[#2D6BFF] text-white"><Plus size={14} className="mr-1" /> Aggiungi</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {project.kpis?.map(k => (
              <div key={k.id} className="cxo-card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900">{k.name}</h4>
                    <p className="text-xs text-slate-500">Aggiornamento: {k.frequency}</p>
                  </div>
                  {user?.role === "admin" && <button onClick={() => deleteKpi(k.id)} className="text-red-400"><Trash2 size={14} /></button>}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-xs text-slate-500">Iniziale</p><p className="font-display font-bold cxo-tabular text-slate-700">{k.initial_value}{k.unit}</p></div>
                  <div><p className="text-xs text-[#2D6BFF]">Attuale</p><p className="font-display font-bold cxo-tabular text-[#2D6BFF]">{k.current_value}{k.unit}</p></div>
                  <div><p className="text-xs text-[#1F9D6A]">Target</p><p className="font-display font-bold cxo-tabular text-[#1F9D6A]">{k.target_value}{k.unit}</p></div>
                </div>
                {k.history?.length > 0 && (
                  <div className="mt-4 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={k.history.map(h => ({ value: h.value, date: h.date?.slice(5, 10) }))}>
                        <CartesianGrid stroke="rgba(100,116,139,0.2)" strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} width={30} />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" stroke="#2D6BFF" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <UpdateKpiBox kpi={k} onUpdate={updateKpi} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-5 space-y-3">
          <div className="cxo-card p-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input placeholder="Nuova attività" value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} />
              <Input type="date" value={newTask.due_date} onChange={e => setNewTask({...newTask, due_date: e.target.value})} />
              <Button onClick={addTask} disabled={!newTask.title} className="bg-[#2D6BFF] text-white"><Plus size={14} className="mr-1" /> Aggiungi task</Button>
            </div>
          </div>
          {project.tasks?.map(t => (
            <div key={t.id} className="cxo-card p-4 flex items-center gap-3">
              <button onClick={() => toggleTask(t.id, t.status === "done" ? "todo" : "done")} className={`w-5 h-5 rounded border-2 flex items-center justify-center ${t.status === "done" ? "bg-[#1F9D6A] border-[#1F9D6A]" : "border-slate-300"}`}>
                {t.status === "done" && <CheckCircle2 size={14} className="text-white" />}
              </button>
              <span className={`flex-1 text-sm ${t.status === "done" ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</span>
              <span className="text-xs text-slate-500">{t.due_date}</span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="time" className="mt-5 space-y-3">
          {(user?.role === "manager" || user?.role === "admin") && (
            <div className="cxo-card p-5">
              <h3 className="text-sm font-semibold text-slate-900">Registra ore/giornate</h3>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
                <Input type="date" value={newTime.date} onChange={e => setNewTime({...newTime, date: e.target.value})} />
                <Input type="number" step="0.5" placeholder="Ore" value={newTime.hours} onChange={e => setNewTime({...newTime, hours: parseFloat(e.target.value) || 0})} />
                <Input placeholder="Descrizione" value={newTime.description} onChange={e => setNewTime({...newTime, description: e.target.value})} />
                <Button onClick={addTimeEntry} className="bg-[#2D6BFF] text-white"><Plus size={14} className="mr-1" /> Registra</Button>
              </div>
            </div>
          )}
          <div className="cxo-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="p-3 text-left">Data</th><th className="p-3 text-left">Ore</th><th className="p-3 text-left">Descrizione</th></tr>
              </thead>
              <tbody>
                {project.time_entries?.map(te => (
                  <tr key={te.id} className="border-t border-slate-100">
                    <td className="p-3 cxo-tabular">{te.date}</td>
                    <td className="p-3 cxo-tabular font-semibold">{te.hours}h</td>
                    <td className="p-3 text-slate-600">{te.description}</td>
                  </tr>
                ))}
                {!project.time_entries?.length && <tr><td colSpan={3} className="p-6 text-center text-slate-500">Nessuna registrazione</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="docs" className="mt-5 space-y-4">
          <div className="cxo-card p-5">
            <h3 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
              <FileText size={18} className="text-[#2D6BFF]" /> Carica un documento di progetto
            </h3>
            <p className="text-sm text-slate-600 mt-1">PDF, presentazioni, fogli di calcolo, immagini. Accessibili solo a chi è nel progetto.</p>
            <div className="mt-4">
              <FileUploader
                category="project_doc"
                projectId={project.id}
                visibility="project"
                buttonLabel="Carica file di progetto"
                onUploaded={() => setDocsKey(k => k + 1)}
              />
            </div>
          </div>
          <div className="cxo-card p-5">
            <h3 className="font-display text-base font-semibold text-slate-900">Documenti del progetto</h3>
            <p className="text-sm text-slate-600 mt-1">Tutti i file condivisi tra azienda, manager e CXOORA.</p>
            <div className="mt-4">
              <FileList projectId={project.id} refreshKey={docsKey} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="feedback" className="mt-5">
          <FeedbackBlock project={project} onUpdate={refresh} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const UpdateKpiBox = ({ kpi, onUpdate }) => {
  const [v, setV] = useState(kpi.current_value);
  const [note, setNote] = useState("");
  return (
    <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
      <Input type="number" step="0.01" value={v} onChange={e => setV(e.target.value)} className="h-9" />
      <Input placeholder="Nota (opzionale)" value={note} onChange={e => setNote(e.target.value)} className="h-9" />
      <Button size="sm" onClick={() => { onUpdate(kpi.id, v, note); setNote(""); }} className="bg-[#2D6BFF] text-white h-9">Aggiorna</Button>
    </div>
  );
};

const FeedbackBlock = ({ project, onUpdate }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [recommended, setRecommended] = useState(true);
  const send = async () => {
    try {
      await api.post(`/projects/${project.id}/reviews`, { rating, comment, recommended });
      toast.success("Feedback inviato");
      setComment("");
      onUpdate();
    } catch (e) { toast.error("Errore"); }
  };
  return (
    <div className="space-y-4">
      <div className="cxo-card p-5">
        <h3 className="font-semibold text-slate-900">Lascia un feedback</h3>
        <div className="mt-3 flex items-center gap-2">
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setRating(n)} className={n <= rating ? "text-[#B7791F]" : "text-slate-300"}>
              <Star size={24} fill="currentColor" />
            </button>
          ))}
        </div>
        <Textarea className="mt-3" rows={3} placeholder="Commento opzionale" value={comment} onChange={e => setComment(e.target.value)} />
        <Button onClick={send} className="mt-3 bg-[#2D6BFF] text-white">Invia feedback</Button>
      </div>
      <div className="space-y-2">
        {project.reviews?.map(r => (
          <div key={r.id} className="cxo-card p-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{r.by_role}</span>
              <span className="flex text-[#B7791F]">{[...Array(r.rating)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</span>
            </div>
            <p className="mt-2 text-sm text-slate-700">{r.comment || "—"}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
